import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { POLLING_INTERVAL } from '../../../global/settings'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useFetchActiveBannerQuery } from 'domain/Banner/bannerApi'

import './bannerNotification.less'

const CATEGORY_CLASS = {
  // Map server banner categories to global banner utility classes
  G: 'banner--info-blue',
  M: 'banner--success-green',
  R: 'banner--warning-yellow',
}

const isExternalLink = url => /^https?:\/\//.test(url || '')

const BannerNotification = () => {
  const { data: banner } = useFetchActiveBannerQuery(undefined, {
    pollingInterval: POLLING_INTERVAL,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  const navigate = useNavigate()
  const { t } = useTranslation()

  const bannerRef = useRef(null)

  const updateBannerHeight = useCallback(() => {
    try {
      const element = bannerRef.current

      if (!element) {
        return
      }

      const height = Math.ceil(element.getBoundingClientRect().height)

      document.body.style.setProperty('--banner-current-height', `${height}px`)
    } catch (error) {
      console.warn('Failed to measure banner height', error)
    }
  }, [])

  useEffect(() => {
    if (!banner) {
      return
    }

    document.body.classList.add('has-banner-notification')

    // Set initial height
    updateBannerHeight()

    // Recalculate on resize for wrapping/dynamic content
    window.addEventListener('resize', updateBannerHeight)

    return () => {
      window.removeEventListener('resize', updateBannerHeight)

      document.body.classList.remove('has-banner-notification')

      document.body.style.removeProperty('--banner-current-height')
    }
  }, [banner, updateBannerHeight])

  const categoryClass = useMemo(
    () => CATEGORY_CLASS[banner?.category] || CATEGORY_CLASS.G,
    [banner?.category]
  )

  const hasContent = Boolean(banner?.content)
  const hasLink = Boolean(banner?.link)

  const externalLink = useMemo(
    () => isExternalLink(banner?.link),
    [banner?.link]
  )

  const handleLinkClick = useCallback(
    event => {
      if (!banner?.link) {
        event.preventDefault()

        return
      }

      event.preventDefault()

      if (externalLink) {
        window.open(banner.link, '_blank', 'noopener,noreferrer')

        return
      }

      navigate(banner.link)
    },
    [banner, externalLink, navigate]
  )

  if (!banner) {
    return null
  }

  const bannerNode = (
    <div
      aria-live="polite"
      className={`banner-notification ${categoryClass}`}
      ref={bannerRef}
      role="status"
    >
      <div className="banner-notification__content">
        <div className="banner-notification__text">
          <span className="banner-notification__title">{banner.title}</span>

          {hasContent && (
            <span className="banner-notification__message-text">
              {banner.content}
            </span>
          )}

          {hasLink && (
            <a
              className="banner-notification__reference-link"
              href={banner.link}
              onClick={handleLinkClick}
              rel="noreferrer"
              target={externalLink ? '_blank' : undefined}
            >
              {t('bannerNotification.referenceLink')}
            </a>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="banner-notification__spacer" />

      {typeof document !== 'undefined'
        ? createPortal(bannerNode, document.body)
        : bannerNode}
    </>
  )
}

export default BannerNotification
